/**
 * InstallmentPreview.jsx
 *
 * FAZ 1 satış sadeleştirme (2026-09-25) — anlık taksit takvimi önizlemesi.
 * `POST /admin/sales/installment-preview` (require_danisman: admin +
 * danışman) DB'ye HİÇBİR ŞEY YAZMAZ — backend'in
 * `services/sales_service.py::compute_equal_installment_schedule` TEK
 * KAYNAK fonksiyonunu çağırır; bu yüzden burada gösterilen tutar/tarihler
 * gerçek satışta yazılacak `installments` satırlarıyla asla ıraksamaz
 * (bkz. backend/BACKEND_GAPS.md "FAZ 1 Satış Sadeleştirme" bölümü).
 *
 * Kullanım: toplam tutar / peşinat / taksit sayısı / ilk taksit tarihi
 * değiştikçe 300ms debounce ile çağrılır. `installments < 2` VE
 * `down_payment <= 0` iken (gösterilecek bir taksit planı yok — tek kalem
 * peşin ödeme) hiçbir şey render ETMEZ (null).
 *
 * Hata mesajları: backend her 400'de `message_tr`/`message_en` döner;
 * burada da uygulama genelindeki AYNI desen kullanılır
 * (`getApiErrorMessage`, bkz. contexts/LanguageContext.js'teki
 * "sales.*" bloğu başındaki not) — kod bazlı ayrı bir eşleme YOKTUR.
 *
 * FAZ 1 düzeltme turu — madde F2 (2026-09-25, `feature/satis-sade-faz1-web`):
 * opsiyonel `editable` prop'u — `true` iken kullanıcı gösterilen her satırın
 * tutarını/vade tarihini elle değiştirebilir ("Eşit Böl" varsayılan olarak
 * kalır, düğme planı sıfırlar). Düzenlenmiş plan
 * `onPlanChange(plan, diff, hasError)` ile PARENT'a bildirilir — bu bileşen
 * hiçbir "Kaydet" butonuna sahip DEĞİLDİR (dört farklı formda gömülü
 * kullanılıyor), parent `diff !== 0 || hasError` iken kendi Kaydet/Dönüştür
 * butonunu pasif bırakmalıdır. `plan===null` demek "kullanıcı dokunmadı,
 * mevcut installments/first_due_date sözleşmesini gönder" demektir —
 * backend'e HİÇBİR YENİ ALAN eklenmez. Düzenleme moduna geçilince backend'in
 * gerçekte uygulayacağı AYNI kontrollerle (bkz.
 * routes/admin.py::admin_sales_installment_preview `plan` alanı, TEK KAYNAK
 * validate_installment_plan_items_or_400) 300ms debounce ile canlı
 * doğrulanır; dönen hata mesajı aynı `getApiErrorMessage` deseniyle
 * gösterilir. Backward-compatible: `editable` verilmezse (varsayılan false)
 * davranış/DOM birebir öncekiyle AYNI kalır.
 *
 * reviewer bulgusu (2026-09-25, Orta) — `hasError` üçüncü argümanı: eskiden
 * kilit yalnızca `diff!==0`'a bakıyordu, canlı doğrulamanın döndürdüğü 400'ü
 * (tarih sırası, ilk tarih kuralı vb.) YOK SAYIYORDU — toplam tutuyorsa ama
 * backend planı reddediyorsa Kaydet/Dönüştür AÇIK kalıyordu. `hasError` planı
 * doğrulama sürerken (`planLoading`) VEYA doğrulama 400 döndürdüğünde
 * (`planError` dolu) `true`'dur; parent bu süre boyunca da butonu kilitli
 * tutmalı.
 */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CalendarClock, Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { fmtMoney } from '../../utils/money';

const DEBOUNCE_MS = 300;

// Küçük, yerel yardımcı — "YYYY-MM-DD" -> "gg.aa.yyyy". Bilerek
// components/crm/DanismanCockpitShared.jsx'teki `fmtDate`'i import ETMEZ:
// o dosya zaten bu bileşeni import ediyor, tersi bir import döngüsü
// açardı. `due_date` alanı backend'den her zaman düz "YYYY-MM-DD" gelir
// (saat/timezone yok), bu yüzden fmtDate'teki offset dallanması gerekmez.
function fmtTRDateOnly(isoDate) {
  if (!isoDate) return '-';
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(isoDate);
  const [, y, mo, da] = m;
  return `${da}.${mo}.${y}`;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default function InstallmentPreview({
  totalAmount, downPayment, installments, firstDueDate, className,
  editable, onPlanChange,
}) {
  const { authHeaders } = useAuth();
  const { t, lang } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  // FAZ 1 düzeltme turu — madde F2: `null` = kullanıcı henüz dokunmadı
  // (eşit bölme geçerli). Dolu dizi = kullanıcının elle düzenlediği satırlar
  // (`[{amount, due_date}]`) — bu haliyle `installment_plan`e gönderilecek.
  const [editedRows, setEditedRows] = useState(null);
  const [planError, setPlanError] = useState('');
  const [planLoading, setPlanLoading] = useState(false);
  const planTimerRef = useRef(null);
  const planReqIdRef = useRef(0);

  const totalNum = Number(totalAmount) || 0;
  const downNum = Number(downPayment) || 0;
  const instNum = Math.trunc(Number(installments)) || 0;

  // Gösterilecek bir taksit planı yok: tek kalem peşin ödeme (installments<2
  // VE peşinat yok). Bu durumda istek hiç atılmaz.
  const shouldPreview = totalNum > 0 && instNum >= 1 && (instNum > 1 || downNum > 0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!shouldPreview) {
      setData(null);
      setError('');
      setLoading(false);
      return undefined;
    }

    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError('');

    timerRef.current = setTimeout(async () => {
      try {
        const res = await apiClient.post(
          '/admin/sales/installment-preview',
          {
            total_amount: totalNum,
            down_payment: downNum,
            installments: Math.max(instNum, 1),
            first_due_date: firstDueDate || null,
          },
          { headers: authHeaders() }
        );
        if (reqIdRef.current === myReqId) {
          setData(res.data);
          setLoading(false);
        }
      } catch (err) {
        if (reqIdRef.current === myReqId) {
          setData(null);
          setError(getApiErrorMessage(err, lang));
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPreview, totalNum, downNum, instNum, firstDueDate, lang]);

  // FAZ 1 düzeltme turu — madde F2: girdiler (tutar/peşinat/taksit sayısı/
  // ilk vade) değişince önceki elle düzenleme anlamsızlaşır — "Eşit Böl"
  // varsayılana döner (kullanıcı isterse tekrar düzenler).
  useEffect(() => {
    if (!editable) return;
    setEditedRows(null);
    setPlanError('');
  }, [editable, totalNum, downNum, instNum, firstDueDate]);

  // Parent'a bildirim: `editedRows===null` -> "dokunulmadı", parent eski
  // installments/first_due_date sözleşmesini kullanmaya devam eder.
  //
  // reviewer bulgusu (2026-09-25, Orta) — `hasError` üçüncü argüman: aşağıdaki
  // canlı doğrulama efekti (`planLoading`/`planError`) BU efektten SONRA
  // tanımlı olsa da aynı render akışında güncellenir — `planLoading`/
  // `planError` bu efeğin bağımlılığı olduğu için doğrulama durumu her
  // değiştiğinde parent TEKRAR bilgilendirilir (plan/diff aynı kalsa bile).
  useEffect(() => {
    if (!editable || !onPlanChange) return;
    if (editedRows === null) {
      onPlanChange(null, 0, false);
      return;
    }
    const base = round2(totalNum - downNum);
    const sum = round2(editedRows.reduce((s, r) => s + (Number(r.amount) || 0), 0));
    const hasError = planLoading || Boolean(planError);
    onPlanChange(
      editedRows.map((r) => ({ amount: Number(r.amount) || 0, due_date: r.due_date })),
      round2(base - sum),
      hasError
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, editedRows, totalNum, downNum, planLoading, planError]);

  // Canlı doğrulama — düzenlenmiş plan, create_sale'in gerçekte uygulayacağı
  // AYNI kontrollerle (routes/admin.py::admin_sales_installment_preview'ın
  // `plan` alanı) 300ms debounce ile doğrulanır. Sonuç yalnızca hata
  // göstermek için kullanılır — editedRows kullanıcının girdiği AYNEN kalır.
  useEffect(() => {
    if (!editable || editedRows === null) {
      setPlanLoading(false);
      return undefined;
    }
    if (planTimerRef.current) clearTimeout(planTimerRef.current);
    const myReqId = ++planReqIdRef.current;
    setPlanLoading(true);
    setPlanError('');
    planTimerRef.current = setTimeout(async () => {
      try {
        await apiClient.post(
          '/admin/sales/installment-preview',
          {
            total_amount: totalNum,
            down_payment: downNum,
            plan: editedRows.map((r) => ({ amount: Number(r.amount) || 0, due_date: r.due_date })),
          },
          { headers: authHeaders() }
        );
        if (planReqIdRef.current === myReqId) setPlanLoading(false);
      } catch (err) {
        if (planReqIdRef.current === myReqId) {
          setPlanError(getApiErrorMessage(err, lang));
          setPlanLoading(false);
        }
      }
    }, DEBOUNCE_MS);
    return () => {
      if (planTimerRef.current) clearTimeout(planTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, editedRows, totalNum, downNum, lang]);

  if (!shouldPreview) return null;

  const equalRows = data?.installments || [];
  const isEdited = editable && editedRows !== null;
  const rows = isEdited ? editedRows : equalRows;
  const rowsTotal = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const installmentBase = round2(totalNum - downNum);
  const diff = round2(installmentBase - rowsTotal);
  const diffIsZero = Math.abs(diff) < 0.01;

  const updateRow = (idx, field, value) => {
    const base = isEdited
      ? editedRows
      : equalRows.map((r) => ({ amount: r.amount, due_date: r.due_date }));
    const next = base.map((r, i) =>
      i === idx ? { ...r, [field]: field === 'amount' ? (parseFloat(value) || 0) : value } : r
    );
    setEditedRows(next);
  };

  const resetToEqualSplit = () => {
    setEditedRows(null);
    setPlanError('');
  };

  return (
    <div
      className={`mt-3 border-2 border-[var(--border-light)] rounded-xl overflow-hidden ${className || ''}`}
      data-testid="installment-preview"
    >
      <div className="px-3 py-2 bg-gray-50 border-b-2 border-[var(--border-light)] flex items-center gap-1.5">
        <CalendarClock size={13} className="text-[var(--text-muted)]" />
        <span className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide flex-1">
          {t('sales.installmentPreview.title')}
        </span>
        {editable && (
          <button
            type="button"
            onClick={resetToEqualSplit}
            disabled={!isEdited}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-[var(--brand-primary)] hover:bg-cyan-50 disabled:opacity-40 disabled:hover:bg-transparent"
            data-testid="installment-preview-reset-btn"
          >
            <RotateCcw size={11} />
            {t('sales.installmentPreview.reset_btn')}
          </button>
        )}
      </div>

      {loading && (
        <div className="p-3 flex items-center gap-2 text-xs text-[var(--text-muted)]" data-testid="installment-preview-loading">
          <Loader2 size={13} className="animate-spin" />
          {t('sales.installmentPreview.loading')}
        </div>
      )}

      {!loading && error && (
        <div
          className="p-3 flex items-center gap-2 text-xs text-rose-600 font-bold"
          data-testid="installment-preview-error"
        >
          <AlertCircle size={13} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <table className="w-full text-sm" data-testid="installment-preview-table">
          <thead className="bg-gray-50/60">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] font-bold text-[var(--text-muted)] uppercase">
                {t('sales.installmentPreview.col_no')}
              </th>
              <th className="text-left px-3 py-1.5 text-[10px] font-bold text-[var(--text-muted)] uppercase">
                {t('sales.installmentPreview.col_due_date')}
              </th>
              <th className="text-right px-3 py-1.5 text-[10px] font-bold text-[var(--text-muted)] uppercase">
                {t('sales.installmentPreview.col_amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-t border-[var(--border-light)]">
                <td className="px-3 py-1.5 text-xs font-semibold">
                  {idx + 1}/{rows.length}
                </td>
                <td className="px-3 py-1.5 text-xs">
                  {editable ? (
                    <input
                      type="date"
                      value={row.due_date || ''}
                      onChange={(e) => updateRow(idx, 'due_date', e.target.value)}
                      className="px-2 py-1 border-2 border-[var(--border-light)] rounded-lg text-xs font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                      data-testid={`installment-preview-due-date-${idx}`}
                      aria-label={`${t('sales.installmentPreview.col_due_date')} ${idx + 1}`}
                    />
                  ) : (
                    fmtTRDateOnly(row.due_date)
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs font-bold text-right">
                  {editable ? (
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={row.amount}
                      onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                      className="w-28 px-2 py-1 border-2 border-[var(--border-light)] rounded-lg text-xs font-bold text-right focus:border-[var(--brand-primary)] focus:outline-none"
                      data-testid={`installment-preview-amount-${idx}`}
                      aria-label={`${t('sales.installmentPreview.col_amount')} ${idx + 1}`}
                    />
                  ) : (
                    fmtMoney(row.amount)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--border-light)] bg-gray-50/60">
              <td colSpan={2} className="px-3 py-1.5 text-xs font-bold">
                {t('sales.installmentPreview.total')}
              </td>
              <td className="px-3 py-1.5 text-xs font-bold text-right">{fmtMoney(rowsTotal)}</td>
            </tr>
            {editable && (
              <tr
                className={`border-t border-[var(--border-light)] ${diffIsZero ? 'bg-emerald-50' : 'bg-rose-50'}`}
                data-testid="installment-preview-diff-row"
              >
                <td
                  colSpan={2}
                  className={`px-3 py-1.5 text-xs font-bold ${diffIsZero ? 'text-emerald-700' : 'text-rose-700'}`}
                >
                  {t('sales.installmentPreview.diff_label')}
                </td>
                <td
                  className={`px-3 py-1.5 text-xs font-bold text-right ${diffIsZero ? 'text-emerald-700' : 'text-rose-700'}`}
                  data-testid="installment-preview-diff-amount"
                >
                  {fmtMoney(diff)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      )}

      {editable && isEdited && !diffIsZero && (
        <p
          className="px-3 py-2 text-[11px] font-bold text-rose-700 bg-rose-50 border-t-2 border-rose-200"
          data-testid="installment-preview-diff-warning"
        >
          <AlertCircle size={11} className="inline mr-1" />
          {t('sales.installmentPreview.diff_warning')}
        </p>
      )}

      {editable && planLoading && (
        <div className="px-3 py-1.5 flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]" data-testid="installment-preview-plan-loading">
          <Loader2 size={11} className="animate-spin" />
          {t('sales.installmentPreview.loading')}
        </div>
      )}

      {editable && !planLoading && planError && (
        <div
          className="px-3 py-2 flex items-center gap-2 text-xs text-rose-600 font-bold border-t-2 border-rose-200"
          data-testid="installment-preview-plan-error"
        >
          <AlertCircle size={13} className="flex-shrink-0" />
          {planError}
        </div>
      )}
    </div>
  );
}

InstallmentPreview.propTypes = {
  totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  downPayment: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  installments: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  firstDueDate: PropTypes.string,
  className: PropTypes.string,
  // FAZ 1 düzeltme turu — madde F2 (2026-09-25): bkz. dosya başı yorumu.
  editable: PropTypes.bool,
  onPlanChange: PropTypes.func,
};

InstallmentPreview.defaultProps = {
  totalAmount: 0,
  downPayment: 0,
  editable: false,
  onPlanChange: null,
  installments: 1,
  firstDueDate: '',
  className: '',
};
